/**
 * Index lookups: `phoneIndex/{phone} → uid` and `plateIndex/{plate} → uid`.
 * These are read-only from the client — writes go through Cloud Functions
 * to keep the indices atomic with the underlying vehicle record.
 */

import { readOnce } from '../../core/firebase.js';
import { normalize as normalizePhone } from '../../shared/phone.js';
import { normalize as normalizePlate } from '../../shared/plate.js';

/**
 * Resolve a phone number to a UID.
 * @param {string} phone
 * @returns {Promise<string | null>}
 */
export async function uidByPhone(phone) {
  const p = normalizePhone(phone);
  if (!p) return null;
  return readOnce(`phoneIndex/${p}`);
}

/**
 * Resolve a plate to its current owner UID.
 * @param {string} plate
 * @returns {Promise<string | null>}
 */
export async function uidByPlate(plate) {
  const p = normalizePlate(plate);
  if (!p) return null;
  return readOnce(`plateIndex/${p}`);
}

/**
 * Convenience: is this phone already claimed by someone other than `expectedUid`?
 * @param {string} phone
 * @param {string} expectedUid
 * @returns {Promise<boolean>}
 */
export async function isPhoneTakenByAnother(phone, expectedUid) {
  const uid = await uidByPhone(phone);
  return Boolean(uid && uid !== expectedUid);
}

/**
 * Convenience: is this plate already claimed by someone other than `expectedUid`?
 * @param {string} plate
 * @param {string} expectedUid
 * @returns {Promise<boolean>}
 */
export async function isPlateTakenByAnother(plate, expectedUid) {
  const uid = await uidByPlate(plate);
  return Boolean(uid && uid !== expectedUid);
}
